/**
 * receivablesAging.ts — Pure computation of the khata (receivables) aging
 * report: who owes the shop, how much, and how long it has been outstanding,
 * bucketed 0–30 / 31–60 / 61–90 / 90+ days and flagged against each customer's
 * credit limit.
 *
 * Design rules:
 *
 *  1. **Aged per invoice, not per customer balance.** `customers.balance` is a
 *     single number with no age, so it cannot produce buckets. The report is
 *     built from unpaid SALE invoices (each carries its own `due_amount` and
 *     date), summed per customer. Voided invoices must already be excluded.
 *  2. **Aged by invoice date, the always-present basis.** Not every credit sale
 *     has a `due_date`, but every invoice has a date. Age = days from the
 *     invoice date to `asOf`. A separate `overdue` figure counts what is past
 *     its `due_date` where one is set — the actionable subset.
 *  3. **Nobody's money is hidden.** An unpaid invoice with no linked customer is
 *     grouped under a single "Unassigned" row rather than dropped.
 *
 * `asOf` is injected (not read from the clock) so the buckets are
 * deterministic and unit-testable. See `receivablesDataSource.ts` for the DB
 * bridge.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Bucket upper bounds in days; the last bucket is everything beyond. */
export const BUCKET_LABELS = ['0–30', '31–60', '61–90', '90+'] as const;
const BUCKET_BOUNDS = [30, 60, 90];

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Whole days between two instants, floored, never negative. */
export function ageInDays(from: Date, asOf: Date): number {
  return Math.max(0, Math.floor((asOf.getTime() - from.getTime()) / DAY_MS));
}

/** 0..3 — which aging bucket a given age in days falls in. */
export function bucketIndex(days: number): number {
  for (let i = 0; i < BUCKET_BOUNDS.length; i++) {
    if (days <= BUCKET_BOUNDS[i]) return i;
  }
  return BUCKET_BOUNDS.length; // 90+
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface ReceivableInvoiceInput {
  customerId: string | null;
  /** Outstanding amount on this invoice (> 0). */
  dueAmount: number;
  /** Invoice date — the aging basis. */
  invoiceDate: Date;
  /** When payment was due, if a term was set. */
  dueDate: Date | null;
}

export interface ReceivableCustomerInput {
  id: string;
  name: string | null;
  phone: string | null;
  creditLimit: number;
}

export interface ReceivablesAgingInput {
  invoices: ReceivableInvoiceInput[];
  customers: ReceivableCustomerInput[];
  /** "Now" — the instant to age against. */
  asOf: Date;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface CustomerReceivable {
  customerId: string | null;
  name: string;
  phone: string | null;
  /** [0–30, 31–60, 61–90, 90+] outstanding amounts. */
  buckets: [number, number, number, number];
  total: number;
  /** Portion past its due_date (where set). */
  overdueAmount: number;
  creditLimit: number;
  /** total exceeds a set credit limit. */
  overLimit: boolean;
  /** Age of this customer's oldest unpaid invoice, in days. */
  oldestDays: number;
  invoiceCount: number;
}

export interface ReceivablesAging {
  rows: CustomerReceivable[];
  totals: {
    buckets: [number, number, number, number];
    total: number;
    overdueAmount: number;
  };
  customerCount: number;
  /** Customers with anything past a due_date. */
  overdueCount: number;
  /** Customers over their credit limit. */
  overLimitCount: number;
}

const UNASSIGNED = 'Unassigned';

export function buildReceivablesAging(input: ReceivablesAgingInput): ReceivablesAging {
  const { invoices, customers, asOf } = input;

  const byId = new Map(customers.map((c) => [c.id, c]));

  interface Acc {
    customerId: string | null;
    buckets: [number, number, number, number];
    overdueAmount: number;
    oldestDays: number;
    invoiceCount: number;
  }
  const groups = new Map<string, Acc>();

  for (const inv of invoices) {
    if (inv.dueAmount <= 0) continue; // defensive; the query already filters
    const key = inv.customerId ?? UNASSIGNED;
    const acc = groups.get(key) ?? {
      customerId: inv.customerId,
      buckets: [0, 0, 0, 0],
      overdueAmount: 0,
      oldestDays: 0,
      invoiceCount: 0,
    };
    const days = ageInDays(inv.invoiceDate, asOf);
    acc.buckets[bucketIndex(days)] += inv.dueAmount;
    if (inv.dueDate && inv.dueDate.getTime() < asOf.getTime()) {
      acc.overdueAmount += inv.dueAmount;
    }
    acc.oldestDays = Math.max(acc.oldestDays, days);
    acc.invoiceCount += 1;
    groups.set(key, acc);
  }

  const rows: CustomerReceivable[] = [...groups.values()].map((acc) => {
    const cust = acc.customerId ? byId.get(acc.customerId) : undefined;
    const buckets = acc.buckets.map(round2) as [number, number, number, number];
    const total = round2(buckets[0] + buckets[1] + buckets[2] + buckets[3]);
    const creditLimit = cust?.creditLimit ?? 0;
    return {
      customerId: acc.customerId,
      name: cust?.name?.trim() || (acc.customerId ? 'Unknown customer' : UNASSIGNED),
      phone: cust?.phone ?? null,
      buckets,
      total,
      overdueAmount: round2(acc.overdueAmount),
      creditLimit,
      overLimit: creditLimit > 0 && total > creditLimit,
      oldestDays: acc.oldestDays,
      invoiceCount: acc.invoiceCount,
    };
  });

  // Largest debtors first — that is who to chase.
  rows.sort((a, b) => b.total - a.total);

  const totals = rows.reduce(
    (t, r) => {
      t.buckets[0] += r.buckets[0];
      t.buckets[1] += r.buckets[1];
      t.buckets[2] += r.buckets[2];
      t.buckets[3] += r.buckets[3];
      t.overdueAmount += r.overdueAmount;
      return t;
    },
    { buckets: [0, 0, 0, 0] as [number, number, number, number], overdueAmount: 0 },
  );
  const totalsBuckets = totals.buckets.map(round2) as [number, number, number, number];

  return {
    rows,
    totals: {
      buckets: totalsBuckets,
      total: round2(totalsBuckets[0] + totalsBuckets[1] + totalsBuckets[2] + totalsBuckets[3]),
      overdueAmount: round2(totals.overdueAmount),
    },
    customerCount: rows.length,
    overdueCount: rows.filter((r) => r.overdueAmount > 0).length,
    overLimitCount: rows.filter((r) => r.overLimit).length,
  };
}
